import i18n from "../../i18n";
/**
 * Salary service — calls the payment-service GraphQL endpoint for
 * Salary and SalaryPayment operations.
 *
 * Pattern: raw fetch, token passed as parameter, URL from env var.
 * businessId is always Number()-coerced before embedding in queries
 * (known ID-vs-Int mismatch on the backend).
 */

import { Salary, SalaryInput, SalaryPayment } from '../../model/payment/Salary';

const API_URL = process.env.REACT_APP_PAYMENT_SERVICE_BASE_URL || '';

async function gqlPost<T>(token: string, query: string): Promise<T> {
    const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, "Accept-Language": i18n.language || "pt",
        },
        body: JSON.stringify({ query }),
    });
    if (!res.ok) {
        throw new Error(`Salary service HTTP ${res.status}`);
    }
    const json = await res.json();
    if (json.errors) {
        const msg = json.errors
            .map((e: { message: string }) => e.message)
            .join(', ');
        throw new Error(msg);
    }
    return json.data as T;
}

const SALARY_FIELDS = `
    id businessId teamMemberId teamMemberName type amount chargesPercent
    description active startDate endDate monthlyCost
`;

const SALARY_PAYMENT_FIELDS = `
    id salaryId businessId periodMonth amount paidAt
`;

/**
 * Fetch all Salary records for a business.
 */
export async function getSalaries(
    businessId: string,
    token: string
): Promise<Salary[]> {
    const id = Number(businessId);
    const data = await gqlPost<{ salaries: Salary[] }>(
        token,
        `query { salaries(businessId: "${id}") { ${SALARY_FIELDS} } }`
    );
    return data.salaries ?? [];
}

/**
 * Fetch paid-salary records for a business in a given month ("YYYY-MM").
 */
export async function getSalaryPayments(
    businessId: string,
    periodMonth: string,
    token: string
): Promise<SalaryPayment[]> {
    const id = Number(businessId);
    const data = await gqlPost<{ salaryPayments: SalaryPayment[] }>(
        token,
        `query { salaryPayments(businessId: "${id}", periodMonth: "${periodMonth}") { ${SALARY_PAYMENT_FIELDS} } }`
    );
    return data.salaryPayments ?? [];
}

/**
 * Create or update a Salary (upsert: pass id to update, omit to create).
 */
export async function saveSalary(
    input: SalaryInput,
    token: string
): Promise<Salary> {
    const id = Number(input.businessId);
    const idField        = input.id          ? `id: "${input.id}"` : '';
    const tmIdField      = input.teamMemberId != null
        ? `teamMemberId: "${input.teamMemberId}"`
        : '';
    const tmNameField    = input.teamMemberName != null
        ? `teamMemberName: "${input.teamMemberName.replace(/"/g, '\\"')}"`
        : '';
    const descField      = input.description != null
        ? `description: "${input.description.replace(/"/g, '\\"')}"`
        : '';
    const endDateField   = input.endDate
        ? `endDate: "${input.endDate}"`
        : '';

    const data = await gqlPost<{ saveSalary: Salary }>(
        token,
        `mutation {
            saveSalary(input: {
                ${idField}
                businessId: "${id}"
                ${tmIdField}
                ${tmNameField}
                type: ${input.type}
                amount: ${input.amount}
                chargesPercent: ${input.chargesPercent}
                ${descField}
                active: ${input.active}
                startDate: "${input.startDate}"
                ${endDateField}
            }) { ${SALARY_FIELDS} }
        }`
    );
    return data.saveSalary;
}

/**
 * Delete a Salary by id.
 */
export async function deleteSalary(
    id: string,
    businessId: string,
    token: string
): Promise<boolean> {
    const bId = Number(businessId);
    const data = await gqlPost<{ deleteSalary: boolean }>(
        token,
        `mutation { deleteSalary(id: "${id}", businessId: "${bId}") }`
    );
    return !!data.deleteSalary;
}

/**
 * Mark a salary as paid for a given month (creates a SalaryPayment record).
 */
export async function markSalaryPaid(
    salaryId: string,
    businessId: string,
    periodMonth: string,
    token: string
): Promise<SalaryPayment> {
    const bId = Number(businessId);
    const data = await gqlPost<{ markSalaryPaid: SalaryPayment }>(
        token,
        `mutation {
            markSalaryPaid(
                salaryId: "${salaryId}",
                businessId: "${bId}",
                periodMonth: "${periodMonth}"
            ) { ${SALARY_PAYMENT_FIELDS} }
        }`
    );
    return data.markSalaryPaid;
}

/**
 * Remove the paid mark for a salary+month (deletes the SalaryPayment record).
 */
export async function unmarkSalaryPaid(
    salaryId: string,
    businessId: string,
    periodMonth: string,
    token: string
): Promise<boolean> {
    const bId = Number(businessId);
    const data = await gqlPost<{ unmarkSalaryPaid: boolean }>(
        token,
        `mutation {
            unmarkSalaryPaid(
                salaryId: "${salaryId}",
                businessId: "${bId}",
                periodMonth: "${periodMonth}"
            )
        }`
    );
    return !!data.unmarkSalaryPaid;
}
